import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { FaChartBar, FaCube, FaCaretRight, FaCaretDown, FaMoneyBill } from 'react-icons/fa';
import '../../styles/Auth/sidebar.css';
import ResetPasswordPage from '../../pages/Auth/Reset-Password/reset-password.jsx';
import { toast } from 'react-toastify';
import { getNavItems } from './navConfig.jsx';
import { useStore } from "../../Context/context.jsx";
import { getData, uploadFile, putData } from "../../webService/webservice.js";
import { API_URL, clearStorageKeepEod } from "../../utils/utils.js";
import Swal from 'sweetalert2';
const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const [showResetModal, setShowResetModal] = useState(false);

  const role = sessionStorage.getItem("role") || localStorage.getItem("role");
  const { selectedStoreId } = useStore();
  const [logoUrl, setLogoUrl] = useState('/assets/logo.png');
  const [isPlaceholder, setIsPlaceholder] = useState(true);

  const ADMIN_ROLES = ["SuperAdmin", "SelfAdmin", "Accountant", "Manager", "StoreCreator"];

  useEffect(() => {
    // Admin roles always show the brand logo — no store-specific logic needed
    if (ADMIN_ROLES.includes(role)) {
      setLogoUrl('/assets/logo.png');
      setIsPlaceholder(false);
      return;
    }

    // Other roles: fetch the store logo or show upload placeholder
    const fetchStoreLogo = async () => {
      if (!selectedStoreId) {
        setLogoUrl(null);
        setIsPlaceholder(true);
        return;
      }
      try {
        const token = sessionStorage.getItem("Token") || localStorage.getItem("Token");
        const response = await getData(`stores/store/${selectedStoreId}`, {}, token);
        if (response?.status === 200 && response?.data) {
          const storeData = response.data;
          if (storeData?.storeLogoImageObject?.uri) {
            setLogoUrl(API_URL + storeData.storeLogoImageObject.uri);
            setIsPlaceholder(false);
          } else if (storeData?.storeImageObject?.uri) {
            setLogoUrl(API_URL + storeData.storeImageObject.uri);
            setIsPlaceholder(false);
          } else {
            setLogoUrl(null);
            setIsPlaceholder(true);
          }
        }
      } catch (error) {
        console.error("Error fetching sidebar logo:", error);
        setLogoUrl(null);
        setIsPlaceholder(true);
      }
    };
    fetchStoreLogo();
  }, [selectedStoreId, role]);

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      toast.error("Image size exceeds the maximum allowed limit (1 MB).");
      event.target.value = "";
      return;
    }

    if (!selectedStoreId) {
      toast.error("No store selected");
      event.target.value = "";
      return;
    }

    try {
      const userId = sessionStorage.getItem("userId") || localStorage.getItem("userId");
      const token = sessionStorage.getItem("Token") || localStorage.getItem("Token");

      toast.info("Uploading logo...");
      // 1. Upload File
      const uploadRes = await uploadFile(`files/upload/${userId}`, file);
      if (uploadRes?.status === 200 || uploadRes?.success) {
        const imageObject = uploadRes.data;

        // 2. Update Store
        const updatePayload = {
          storeLogoImageObject: imageObject
        };

        const updateRes = await putData(`stores/store/${selectedStoreId}`, updatePayload, token);
        if (updateRes?.status === 200 || updateRes?.success) {
          toast.success("Store logo updated successfully!");
          setLogoUrl(API_URL + imageObject.uri);
          setIsPlaceholder(false);
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          toast.error("Failed to update store details");
        }
      } else {
        toast.error("Failed to upload image");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error uploading logo");
    }
  };

  const toggleSubmenu = (label) => {
    console.log('Toggling submenu for:', label);
    setOpenSubmenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const confirmLogout = () => {
    clearStorageKeepEod();
    toast.success('Logout successful!');
    navigate('/login');
  };

  const handleLogoutClick = async () => {
    const token = sessionStorage.getItem("Token") || localStorage.getItem("Token");
    if (ADMIN_ROLES.includes(role) || !selectedStoreId) {
      setShowLogoutModal(true);
      return;
    }

    try {
      const res = await getData(
        "eod/opening-cash-status",
        {
          storeId: selectedStoreId
        },
        token
      );

      if (res?.data?.isTodayClosed === true) {
        setShowLogoutModal(true);
      } else {
        const result = await Swal.fire({
          icon: "warning",
          title: "EOD Closing Pending",
          text: "You have not completed the End of Day (EOD) Closing for today. Please complete the EOD Closing before logging out.",
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Go to EOD Report",
          denyButtonText: "Logout Anyway",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#3C0008",
          denyButtonColor: "#64748b",
          cancelButtonColor: "#d33",
          allowOutsideClick: false
        });

        if (result.isConfirmed) {
          navigate("/reports/eod");
          if (onClose) onClose();
        } else if (result.isDenied) {
          confirmLogout();
        }
      }
    } catch (err) {
      console.error("Error checking EOD status on logout:", err);
      setShowLogoutModal(true);
    }
  };

  const adminStoreIds = JSON.parse(sessionStorage.getItem("storeIds") || localStorage.getItem("storeIds") || "[]");
  const [hoverLogo, setHoverLogo] = useState(false);

  // --- Main Navigation Items ---
  const navItems = getNavItems(role) || [];

  const filteredNavItems = navItems.filter(item => {
    if (item.hidden || item.path === "/logout") return false;

    // 🔒 RESTRICT POS MENU VISIBILITY
    if (item.path === "/pos") {
      const hasPosAccess = localStorage.getItem("hasPosAccess") === "true" || sessionStorage.getItem("hasPosAccess") === "true";

      // 1. Must have explicit POS access assigned
      if (!hasPosAccess) return false;

      // 2. Certain roles must have at least one store assigned
      const STORE_DEPENDENT_ROLES = ["FranchiseAdmin", "ClusterManager", "StoreAdmin", "StoreManager", "Biller", "Manager", "Accountant"];
      if (STORE_DEPENDENT_ROLES.includes(role) && adminStoreIds.length === 0) return false;
    }

    return true;
  });

  const logoutItem = navItems.find(item => item.path === "/logout");

  return (
    <div className={`sidebar-container ${isOpen ? 'open' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar-logo" style={{ position: 'relative' }}>
          <div className="logo-glow"></div>
          {isPlaceholder ? (
            /* ---- NO LOGO: Show upload placeholder ---- */
            <label
              htmlFor="sidebar-logo-upload"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '80%',
                margin: '0 auto',
                minHeight: '50px',
                border: '2px dashed rgba(255, 255, 255, 1)',
                borderRadius: '10px',
                cursor: 'pointer',
                gap: '6px',
                padding: '10px 0',
                transition: 'border-color 0.2s',
              }}
            >
              <Icon icon="mdi:image-plus-outline" style={{ fontSize: '28px', color: 'rgba(255,255,255,0.8)', display: 'block', margin: '0 auto' }} />
              <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.65)', fontWeight: 600, letterSpacing: '0.5px', textAlign: 'center', display: 'block', width: '100%' }}>
                Upload Store Logo
              </span>
              <input
                type="file"
                id="sidebar-logo-upload"
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleLogoUpload}
              />
            </label>
          ) : (
            /* ---- HAS LOGO: Show logo with edit overlay on hover ---- */
            <div
              style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', cursor: 'pointer' }}
              onMouseEnter={() => setHoverLogo(true)}
              onMouseLeave={() => setHoverLogo(false)}
            >
              <img
                src={logoUrl}
                alt="Store Logo"
                className="dynamic-logo-img"
                style={{ borderRadius: '8px', display: 'block', transition: 'opacity 0.2s', opacity: hoverLogo ? 0.5 : 1 }}
              />
              {hoverLogo && (
                <label
                  htmlFor="sidebar-logo-edit"
                  style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', gap: '4px', borderRadius: '8px',
                  }}
                >
                  <Icon icon="mdi:pencil-circle-outline" style={{ fontSize: '26px', color: '#fff' }} />
                  <span style={{ fontSize: '9px', color: '#fff', fontWeight: 700, letterSpacing: '0.5px' }}>Edit Logo</span>
                  <input
                    type="file"
                    id="sidebar-logo-edit"
                    style={{ display: 'none' }}
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                </label>
              )}
            </div>
          )}
        </div>

        <div className="sidebar-nav-container">
          <ul className="sidebar-nav">
            {filteredNavItems.map(({ path, label, icon, subItems }) => (
              <li key={label} className={path && location.pathname.startsWith(path) ? 'active' : ''}>
                <div
                  className="sidebar-link"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    if (path) {
                      navigate(path);
                      if (onClose) onClose();
                    }
                  }}
                >
                  {/* Main content - navigates to page */}
                  <div
                    className="sidebar-link-content"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      flex: 1,
                      cursor: 'pointer'
                    }}
                  >
                    <span className="icon">{icon}</span>
                    <span className="link-text">{label}</span>
                  </div>

                  {/* Caret (toggle submenu) */}
                  {subItems && (
                    <div
                      style={{ cursor: 'pointer', fontSize: '16px', width: '16px', height: '16px' }}
                      onClick={(e) => {
                        e.stopPropagation();      // ✅ Prevent row navigation
                        toggleSubmenu(label);    // ✅ Only toggle submenu
                      }}
                    >
                      {openSubmenus[label] ?
                        <FaCaretDown style={{ fontSize: '16px' }} /> :
                        <FaCaretRight style={{ fontSize: '16px' }} />
                      }
                    </div>
                  )}
                </div>


                {/* Submenu */}
                {subItems && (
                  <ul className={`submenu ${openSubmenus[label] ? 'open' : ''}`}>
                    {subItems.map((sub) => (
                      <li key={sub.path}>
                        <Link
                          to={sub.path}
                          className={`submenu-link ${location.pathname === sub.path ? 'active-sub' : ''}`}
                          onClick={() => {
                            if (onClose) onClose();
                          }}
                        >
                          <span className="icon">{sub.icon}</span>
                          <span>{sub.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Fixed Logout at Bottom */}
        {logoutItem && (
          <div className="sidebar-footer">
            <div
              className="sidebar-link logout-item"
              onClick={handleLogoutClick}
            >
              <span className="icon">{logoutItem.icon}</span>
              <span className="link-text">{logoutItem.label}</span>
            </div>
          </div>
        )}
      </aside>


      {showLogoutModal && (
        <div className="logout-overlay">
          <div className="logout-modal">

            <button
              className="logout-close"
              onClick={() => setShowLogoutModal(false)}
            >
              &times;
            </button>

            <div className="logout-icon-box">
              <Icon icon="mdi:logout" width="50" height="50" className="logout-icon" />
            </div>

            <h2 className="logout-title">Confirm Logout</h2>
            <p className="logout-text">Are you sure you want to sign out from your account?</p>

            <div className="logout-actions">
              <button className="btn-confirm" onClick={confirmLogout}>
                Yes, Logout
              </button>
              <button className="btn-cancel" onClick={() => setShowLogoutModal(false)}>
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}


      {/* Reset Password Modal */}
      {showResetModal && <ResetPasswordPage onClose={() => setShowResetModal(false)} />}
    </div>
  );
};

export default Sidebar;
